import { type FC, type KeyboardEvent, type ReactNode, useRef, useState } from "react"

import { ModalBase } from "./ModalBase"
import UbiqButton, { UbiqButtonSize } from "./UbiqButton"

function renderMarkdownLinks(text: string): ReactNode[] | string {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let key = 0

  for (let match = regex.exec(text); match !== null; match = regex.exec(text)) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>)
    }
    parts.push(
      <a
        className="font-bold underline"
        href={match[2]}
        key={key++}
        onClick={(e) => e.stopPropagation()}
        rel="noreferrer"
        target="_blank"
      >
        {match[1]}
      </a>,
    )
    lastIndex = match.index + match[0].length
  }

  if (lastIndex === 0) return text

  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>)
  }

  return parts
}

/**
 * Like renderMarkdownLinks, but every markdown link becomes a trigger for `onTrigger`
 * (the link target is ignored). When the label has no link, the whole label is the trigger.
 */
function renderModalTriggerLinks(text: string, onTrigger: () => void): ReactNode[] {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let key = 0

  const trigger = (label: string) => (
    <button
      className="font-bold underline"
      key={key++}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onTrigger()
      }}
      type="button"
    >
      {label}
    </button>
  )

  for (let match = regex.exec(text); match !== null; match = regex.exec(text)) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>)
    }
    parts.push(trigger(match[1]))
    lastIndex = match.index + match[0].length
  }

  if (lastIndex === 0) return [trigger(text)]

  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>)
  }

  return parts
}

export interface JoinRequirementFieldConfig {
  id: string
  type: "checkbox_url" | "checkbox_text" | "text_input" | "textarea" | "date" | "email"
  label: string
  placeholder?: string
  /** Plain text shown in a popup for `checkbox_text` fields (line breaks preserved). */
  modalContent?: string
  required: boolean
}

interface JoinRequirementsFieldsProps {
  fields: JoinRequirementFieldConfig[]
  values: Record<string, unknown>
  errors: Record<string, string>
  onChange: (fieldId: string, value: unknown) => void
  className?: string
  labelClassName?: string
  /** The join modal centers checkbox rows; checkout lines them up with the rest of its form. */
  checkboxAlign?: "center" | "start"
}

export const JoinRequirementsFields: FC<JoinRequirementsFieldsProps> = ({
  fields,
  values,
  errors,
  onChange,
  className = "",
  labelClassName = "uppercase",
  checkboxAlign = "center",
}) => {
  const formRef = useRef<HTMLDivElement>(null)
  const [modalText, setModalText] = useState<string | null>(null)

  const focusNextField = (currentIndex: number) => {
    if (!formRef.current) return
    const inputs = formRef.current.querySelectorAll<HTMLElement>("input:not(.sr-only), textarea")
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].dataset.fieldIndex === String(currentIndex)) {
        inputs[i + 1]?.focus()
        return
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent, fieldIndex: number) => {
    if (e.key === "Enter") {
      e.preventDefault()
      focusNextField(fieldIndex)
    }
  }

  const inputBaseStyles =
    "w-full rounded-[4px] border px-4 text-body-md font-mono outline-none bg-transparent placeholder:text-textSecondary"
  const inputStateStyles = "border-borderPrimaryLight focus:border-borderPrimary"
  const textareaBaseStyles = `${inputBaseStyles} min-h-[112px] resize-none py-3`

  const inputFields = fields.filter((f) => f.type !== "checkbox_url" && f.type !== "checkbox_text")

  const renderCheckbox = (
    field: JoinRequirementFieldConfig,
    value: unknown,
    error: string | undefined,
    label: ReactNode,
  ) => (
    <div
      className={`flex flex-col ${checkboxAlign === "start" ? "items-start" : "items-center"}`}
      key={field.id}
    >
      <label
        className={`inline-flex items-center justify-center gap-3 text-center cursor-pointer ${
          error ? "text-error" : "text-textPrimary"
        }`}
      >
        <input
          checked={!!value}
          className="sr-only"
          onChange={(e) => onChange(field.id, e.target.checked)}
          type="checkbox"
        />
        <span
          className={`block h-6 w-6 min-w-6 min-h-6 rounded-[4px] border bg-center bg-no-repeat ${
            value
              ? "border-transparent bg-[url('/assets/images/icons/checkbox.svg')]"
              : error
                ? "border-error"
                : "border-borderPrimary"
          }`}
        />
        <span className={`text-body-lg font-mono text-left ${labelClassName}`}>{label}</span>
      </label>
      {error && <p className="mt-1 text-center text-body-sm text-error">{error}</p>}
    </div>
  )

  return (
    <div className={`flex w-full flex-col gap-5 ${className}`} ref={formRef}>
      {fields.map((field, index) => {
        const error = errors[field.id]
        const value = values[field.id]

        switch (field.type) {
          case "text_input":
            return (
              <div className="flex flex-col" key={field.id}>
                <input
                  className={`h-12 ${inputBaseStyles} ${
                    error
                      ? "border-error text-error placeholder:text-error placeholder:opacity-40"
                      : `${inputStateStyles} text-textPrimary`
                  }`}
                  data-field-index={index}
                  enterKeyHint={index < inputFields.length - 1 ? "next" : "done"}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  placeholder={field.placeholder ?? field.label}
                  type="text"
                  value={(value as string) ?? ""}
                />
                {error && <p className="mt-1 text-body-sm text-error">{error}</p>}
              </div>
            )

          case "textarea":
            return (
              <div className="flex flex-col" key={field.id}>
                <textarea
                  className={`${textareaBaseStyles} ${
                    error
                      ? "border-error text-error placeholder:text-error placeholder:opacity-40"
                      : `${inputStateStyles} text-textPrimary`
                  }`}
                  data-field-index={index}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  placeholder={field.placeholder ?? field.label}
                  value={(value as string) ?? ""}
                />
                {error && <p className="mt-1 text-body-sm text-error">{error}</p>}
              </div>
            )

          case "email":
            return (
              <div className="flex flex-col" key={field.id}>
                <input
                  className={`h-12 ${inputBaseStyles} ${
                    error
                      ? "border-error text-error placeholder:text-error placeholder:opacity-40"
                      : `${inputStateStyles} text-textPrimary`
                  }`}
                  data-field-index={index}
                  enterKeyHint={index < inputFields.length - 1 ? "next" : "done"}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  placeholder={field.placeholder ?? field.label}
                  type="email"
                  value={(value as string) ?? ""}
                />
                {error && <p className="mt-1 text-body-sm text-error">{error}</p>}
              </div>
            )

          case "date":
            return (
              <div className="flex flex-col" key={field.id}>
                <input
                  className={`h-12 ${inputBaseStyles} ${
                    error
                      ? "border-error text-error placeholder:text-error placeholder:opacity-40"
                      : `${inputStateStyles} text-textPrimary`
                  }`}
                  data-field-index={index}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  placeholder={field.label}
                  type="date"
                  value={(value as string) ?? ""}
                />
                {error && <p className="mt-1 text-body-sm text-error">{error}</p>}
              </div>
            )

          case "checkbox_url":
            return renderCheckbox(field, value, error, renderMarkdownLinks(field.label))

          case "checkbox_text":
            return renderCheckbox(
              field,
              value,
              error,
              renderModalTriggerLinks(field.label, () => setModalText(field.modalContent ?? "")),
            )

          default:
            return null
        }
      })}

      <ModalBase
        addBgOpacity
        dialogPanelStyles="max-w-[720px] pt-10 sm:pt-16 pb-6 w-full max-h-[80vh]"
        onClose={() => setModalText(null)}
        show={modalText !== null}
      >
        <div className="flex flex-col max-h-[calc(80vh-6rem)]">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-10">
            <p className="whitespace-pre-wrap text-body-lg text-textPrimary">{modalText}</p>
          </div>
          <div className="flex-shrink-0 pt-4 pb-2 px-4">
            <UbiqButton
              onClick={() => setModalText(null)}
              size={UbiqButtonSize.MEDIUM}
              styles="ml-auto sm:mr-[22px]"
              title="GOT IT"
            />
          </div>
        </div>
      </ModalBase>
    </div>
  )
}
